import { useEffect, useState } from "react";
import { getOrganizationCampaigns_api } from "api/organizationApi";
import { CurrentBrand, OrganizationCampaign } from "modules/Types";
import { Body2, Caption } from "components/shareable/Typography";
import useCurrentBrand from 'Hooks/useCurrentBrand';
import { useHistory } from "react-router-dom";
import { toastError } from "modules/Toast";
import OrganizationTabSection from "../OrganizationTabSection";
import { GridColDef } from "@mui/x-data-grid";
import OrganizationViewDefaultGrid from "../componentsOrganization/OrganizationViewDefaultGrid";
import dayjs from "dayjs";
import Axios from "helpers/Interceptor";
import getSymbolFromCurrency from "currency-symbol-map";
import { getActualStatus} from '../Organization_utilsCampaignsTab';
import BrandSelectionModal from "components/shareable/CreateCampaignModal/SelectBrandModal";
import TableEmptyState from "./OrganizationTabEmptyState";
import Colors from "modules/Colors";
import useOrganization from "Hooks/useOrganization";
import OrganizationTabLoading from "../componentsOrganization/OrganizationTabLoading";
import TagSymphony from "components/shareable/TagSymphony";
import { 
  CAMPAIGN_STATUS_INFO, 
  CampaignStatus, 
  PLAYBOOK_TYPE_INFO, 
  PlaybookType 
} from "modules/Const";
import { TagColors } from "elements/TagSymphony";

const PAGE_SIZE = 8;

interface Props {
  organizationBrands: CurrentBrand[];
}

const OrganizationTabCampaigns = ({ organizationBrands }: Props) => {
  const history = useHistory();
  const { currentBrand, setCurrentBrand } = useCurrentBrand();
  const { isLoadingTab, setIsLoadingTab, getOrganizationId } = useOrganization();
  const organizationId = getOrganizationId();

  const [campaigns, setCampaigns] = useState<OrganizationCampaign[]>([]);
  const [totalCampaigns, setTotalCampaigns] = useState<number>(0);
  const [page, setPage] = useState<number>(0);

  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isCreateCampaignModalOpen, setIsCreateCampaignModalOpen] = useState(false);

  const fetchCampaigns = async (page: number) => {
    setIsLoadingTab(true);
    const offset = page * PAGE_SIZE;
    const response = await getOrganizationCampaigns_api(organizationId, offset, PAGE_SIZE);
    if (response) {
      setCampaigns(response.campaigns);
      setTotalCampaigns(response.total);
    }
    setIsLoadingTab(false);
  };

  useEffect(() => {
    fetchCampaigns(page);
  }, [page]);


  const handleBrandSelection = async ({ brandId, campaignId }: { brandId: number, campaignId: number }) => {
    try {
      if (currentBrand?.id === brandId) {
        history.push({
          pathname: `/marketing/campaigns/${campaignId}`,
          state: { from: 'TEAM' }
        });
      }

      setIsLoadingTab(true)
      const response = await Axios.get(`/brand/${brandId}`);
      const newBrand = response.data as CurrentBrand;
      if (newBrand) {
        setCurrentBrand(newBrand);
        history.push({
          pathname: `/marketing/campaigns/${campaignId}`,
          state: { from: 'TEAM' }
        });
      } else {
        throw new Error("Brand not found");
      }
    } catch (error) {
      toastError("Error getting brand information");
    } finally {
      setIsLoadingTab(false)
    }
  }

  const columns: GridColDef[] = [
    {
      field: "profile",
      headerName: "Profile",
      flex: 1.5,
      renderCell: (params) => (
        <div className="flex items-center gap-3">
          <img
            src={params.row.brandImage}
            alt={params.row.brandName}
            className="w-10 h-10 rounded-full object-cover"
          />
          <Body2>{params.row.brandName}</Body2>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const actualStatus = getActualStatus(params.row);
        return (
          <TagSymphony
            className="teamCampaignStatusTag"
            color={CAMPAIGN_STATUS_INFO[actualStatus as CampaignStatus].color as TagColors}
            title={CAMPAIGN_STATUS_INFO[actualStatus as CampaignStatus].title}
          />
        );
      },
    },
    {
      field: "name",
      headerName: "Campaign Name",
      flex: 2,
      renderCell: (params) => (
        <div className="flex flex-col gap-1">
          <Body2>{params.row.campaignName}</Body2>
          <TagSymphony
            className="teamCampaignTypeTag"
            title={PLAYBOOK_TYPE_INFO[params.row.type as PlaybookType].title}
            icon={PLAYBOOK_TYPE_INFO[params.row.type as PlaybookType].icon}
          />
        </div>
      ),
    },
    {
      field: "budget",
      headerName: "Budget",
      flex: 1,
      renderCell: (params) => (
        <Body2>
          {params.row.budget
            ? `${getSymbolFromCurrency(params.row.currency)}${Number(params.row.budget).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '-'}
        </Body2>
      ),
    },
    {
      field: "length",
      headerName: "Campaign Length",
      flex: 2,
      renderCell: (params) => {
        const startDate = params.row.startDate ? dayjs(params.row.startDate).format('MMM DD, YYYY') : '-';
        const endDate = params.row.endDate ? dayjs(params.row.endDate).format('MMM DD, YYYY') : '-';
        const length = params.row.startDate && params.row.endDate
          ? `${dayjs(params.row.endDate).diff(dayjs(params.row.startDate), 'day')} days`
          : '-';

        return (
          <div className="flex flex-col gap-1">
            <Body2>{`${startDate} - ${endDate}`}</Body2>
            <Caption color={Colors.greyDark}>{length}</Caption>
          </div>
        );
      },
    },
  ];

  const handleBrandSelect = (brandId: number) => {
    const selectedBrand = organizationBrands.find(brand => brand.id === brandId);
    if (selectedBrand) {
      setCurrentBrand(selectedBrand);
      setIsBrandModalOpen(false);
      setIsCreateCampaignModalOpen(true);
    }
  };

  if (isLoadingTab) {
    return <OrganizationTabLoading />
  }

  return (
    <>
      <OrganizationTabSection
        className="organizationPageMarketingTab"
        buttonClassName="organizationPageCreateCampaignButton"
        title="Marketing Campaigns"
        buttonText="Create a Campaign"
        onButtonClick={() => setIsBrandModalOpen(true)}
        showDividers
      >
        <OrganizationViewDefaultGrid
          className="organizationMarketingGrid"
          rows={campaigns}
          columns={columns}
          onRowClick={({ row }) => {
            handleBrandSelection({ brandId: row.brandId, campaignId: row.id })
          }}
          hideFooter={false}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          itemsPerPage={PAGE_SIZE}
          rowCount={totalCampaigns}
          itemName="Campaigns"
          // isLoadingData={isLoading}
          paginationMode="server"
          emptyStateComponent={
            <TableEmptyState
              className="organizationMarketingTableEmptyState"
              style={{ marginTop: 10 }}
              primaryText="One place to view and manage your team's campaigns."
              buttonText="Create Your First Campaign"
              buttonAction={() => setIsBrandModalOpen(true)}
            />
          }
        />
      </OrganizationTabSection>

      <BrandSelectionModal
        className="organizationMarketingTabBrandSelectionModal"
        isOpen={isBrandModalOpen}
        onClose={() => setIsBrandModalOpen(false)}
        onBrandSelect={handleBrandSelect}
        titleText="Select a Profile"
        subtitleText="Choose the profile you want to create a campaign for."
      />
    </>
  );
};

export default OrganizationTabCampaigns;